import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Flex,
  useToast,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { MdOutlineSmartToy } from 'react-icons/md';
import { RiAddLine, RiCheckLine } from 'react-icons/ri';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import {
  useGetMyCustomAgentsLazyQuery,
  useAddAgentToProjectMutation,
} from '../../generated/graphql';
import { useEffect, useState } from 'react';

interface AddAgentToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  existingAgentIds: Set<string>;
  // Called after an agent is successfully staged, so the parent can refetch
  // (e.g. to show the "staged for next update" banner).
  onAgentAdded?: () => void;
}

export const AddAgentToProjectModal = ({
  isOpen,
  onClose,
  projectId,
  existingAgentIds,
  onAgentAdded,
}: AddAgentToProjectModalProps) => {
  const toast = useToast();
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

  const [fetchAgents, { data, loading }] = useGetMyCustomAgentsLazyQuery();
  const [addAgentToProject, { loading: adding }] = useAddAgentToProjectMutation();

  useEffect(() => {
    if (isOpen) {
      fetchAgents({ variables: { ownedOnly: false } });
      setAddedIds(new Set());
    }
  }, [isOpen, fetchAgents]);

  const agents = data?.getMyCustomAgents ?? [];

  // Filter out agents already in the project (from perspectives or just added)
  const availableAgents = agents.filter(
    (a) => a.status === 'ACTIVE' && !existingAgentIds.has(a.id) && !addedIds.has(a.id)
  );
  const justAdded = agents.filter((a) => addedIds.has(a.id));

  const handleAdd = async (agentId: string, agentName: string) => {
    try {
      await addAgentToProject({
        variables: { agentId, projectId },
      });

      setAddedIds((prev) => {
        const next = new Set(Array.from(prev));
        next.add(agentId);
        return next;
      });

      toast({
        title: 'Agent staged',
        description: `"${agentName}" will be included on the next update.`,
        status: 'success',
        duration: 4000,
      });
      onAgentAdded?.();
    } catch (error: any) {
      toast({
        title: 'Error adding agent',
        description: error.message || 'Something went wrong.',
        status: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent bg={HelivanColor.background.secondary} borderRadius="12px">
        <ModalHeader
          py={4}
          px={5}
          bg="white"
          borderBottom="1px solid"
          borderColor={HelivanColor.border.primary}
          borderTopRadius="12px"
        >
          <HStack spacing={3}>
            <Flex
              w="36px"
              h="36px"
              borderRadius="8px"
              bg={HelivanColor.hover.lightBlue}
              align="center"
              justify="center"
            >
              <MdOutlineSmartToy size={18} color={HelivanColor.text.helivanBlue} />
            </Flex>
            <Text fontSize="15px" fontWeight={600} color={HelivanColor.text.blueDark}>
              Add Agent to Project
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />
        <ModalBody py={4} px={4}>
          <VStack spacing={3} align="stretch">
            <HStack spacing={2} px={1}>
              <Text fontSize="12px" color={HelivanColor.text.blueMid}>
                Added agents will be surveyed in the next update
              </Text>
            </HStack>

            {loading ? (
              <Flex justify="center" align="center" py={8}>
                <Spinner color={HelivanColor.text.helivanBlue} />
              </Flex>
            ) : availableAgents.length === 0 && justAdded.length === 0 ? (
              <Flex justify="center" align="center" py={8}>
                <Text fontSize="13px" color={HelivanColor.text.blueMid}>
                  No agents available to add.
                </Text>
              </Flex>
            ) : (
              <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto">
                {/* Agents just added in this session */}
                {justAdded.map((agent) => (
                  <HStack
                    key={agent.id}
                    p={3}
                    borderRadius="8px"
                    border="1px solid"
                    borderColor={HelivanColor.text.helivanBlue}
                    bg="rgba(0, 71, 125, 0.05)"
                    justifyContent="space-between"
                  >
                    <HStack spacing={3}>
                      <MdOutlineSmartToy size={18} color={HelivanColor.text.helivanBlue} />
                      <VStack align="start" spacing={0}>
                        <HelivanText variant="smallBody">{agent.name}</HelivanText>
                        <Text fontSize="12px" color={HelivanColor.text.blueMid}>
                          Will be included in next update
                        </Text>
                      </VStack>
                    </HStack>
                    <RiCheckLine size={16} color={HelivanColor.text.helivanBlue} />
                  </HStack>
                ))}

                {/* Available agents */}
                {availableAgents.map((agent) => (
                  <HStack
                    key={agent.id}
                    p={3}
                    borderRadius="8px"
                    border="1px solid"
                    borderColor={HelivanColor.border.primary}
                    bg="white"
                    _hover={{ bg: HelivanColor.hover.lightBlue, borderColor: HelivanColor.text.helivanBlue }}
                    cursor="pointer"
                    onClick={() => handleAdd(agent.id, agent.name)}
                    justifyContent="space-between"
                    transition="all 0.15s"
                  >
                    <HStack spacing={3}>
                      <MdOutlineSmartToy size={18} color={HelivanColor.text.blueMid} />
                      <VStack align="start" spacing={0}>
                        <HelivanText variant="smallBody">{agent.name}</HelivanText>
                        {agent.description && (
                          <Text fontSize="12px" color={HelivanColor.text.blueMid} noOfLines={1}>
                            {agent.description}
                          </Text>
                        )}
                      </VStack>
                    </HStack>
                    {adding ? (
                      <Spinner size="sm" color={HelivanColor.text.helivanBlue} />
                    ) : (
                      <RiAddLine size={16} color={HelivanColor.text.blueMid} />
                    )}
                  </HStack>
                ))}
              </VStack>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
